import {
  $computeTableMap,
  $createTableCellNode,
  $createTableSelection,
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $findCellNode,
  $findTableNode,
  $getNodeTriplet,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  $isTableSelection,
  getTableElement,
  getTableObserverFromTableElement,
  INSERT_TABLE_COMMAND,
  InsertTableCommandPayload,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableSelection
} from '@lexical/table'
import { $insertFirst } from '@lexical/utils'
import {
  $createParagraphNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $setSelection,
  BaseSelection,
  LexicalEditor,
  LexicalNode,
  RangeSelection
} from 'lexical'

import { TABLE_MARK_UPDATE_COMMAND } from '../plugins/TableControllerPlugin/commands'

import { $focusNode, $findParentNode, $findParentNodeOrThrow } from './node'
import { $selectLastDescendant } from './selection'

export const getTableElementOrThrow = (
  editor: LexicalEditor,
  tableNode: TableNode
) => {
  const tableElement = getTableElement(
    tableNode,
    editor.getElementByKey(tableNode.getKey())
  )
  if (!tableElement) {
    throw new Error('tableElement not found')
  }
  return tableElement
}

export const getTableObserverFromOrThrow = (
  editor: LexicalEditor,
  tableNode: TableNode
) => {
  const tableElement = getTableElementOrThrow(editor, tableNode)
  const tableObserver = getTableObserverFromTableElement(tableElement)
  if (!tableObserver) {
    throw new Error('tableObserver not found')
  }
  return tableObserver
}

/**
 * 计算选区中的行列数
 */
export const computeTableSelectionCounts = (selection: TableSelection) => {
  const selectionShape = selection.getShape()
  return {
    cols: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1
  }
}

/**
 * 获取选区中的单元格节点
 */
export const $getTableCellNodes = (selection: BaseSelection | null) => {
  if ($isRangeSelection(selection)) {
    const tableCellNode = $findParentNode(
      selection.anchor.getNode(),
      $isTableCellNode
    )
    return tableCellNode ? [tableCellNode] : []
  }
  if ($isTableSelection(selection)) {
    const nodes = selection.getNodes()
    return nodes.filter($isTableCellNode)
  }
  return []
}

/**
 * 判断表格是否聚焦
 */
export const $isTableNodeFocusedAtSelection = () => {
  const selection = $getSelection()
  if ($isTableSelection(selection)) {
    return true
  }
  if ($isRangeSelection(selection)) {
    return $isNodeInsideTable(selection.anchor.getNode())
  }
  return false
}

/**
 * 判断节点是否在表格中
 */
export const $isNodeInsideTable = (node: LexicalNode | null) => {
  const tableNode = $findParentNode(node, $isTableNode)
  return !!tableNode
}

/**
 * 通过选区获取表格节点
 */
export const $getTableNodeAtSelection = () => {
  const selection = $getSelection()
  if ($isTableSelection(selection) || $isRangeSelection(selection)) {
    return $findParentNode(selection.anchor.getNode(), $isTableNode)
  }
  return undefined
}

/**
 * 插入表格行
 */
export const $insertTableRow = ({
  shouldInsertAfter,
  rowCount
}: {
  shouldInsertAfter: boolean
  rowCount: number
}) => {
  for (let i = 0; i < rowCount; i++) {
    $insertTableRowAtSelection(shouldInsertAfter)
  }
}

/**
 * 插入表格列
 */
export const $insertTableCol = ({
  shouldInsertAfter,
  columnCount
}: {
  shouldInsertAfter: boolean
  columnCount: number
}) => {
  for (let i = 0; i < columnCount; i++) {
    $insertTableColumnAtSelection(shouldInsertAfter)
  }
}

/**
 * 删除当前选区中的表格行
 */
export const $deleteTableRowsAtSelection = () => {
  $deleteTableRowAtSelection()
}

/**
 * 删除当前选区中的表格列
 */
export const $deleteTableColsAtSelection = () => {
  $deleteTableColumnAtSelection()
}

/**
 * 通过 TableCellNode 的 key 删除表格
 */
export const $deleteTableAtSelection = (editor: LexicalEditor) => {
  const tableNode = $getTableNodeAtSelection()
  if (!tableNode) return
  const tableObserver = getTableObserverFromOrThrow(editor, tableNode)
  tableObserver.$clearHighlight()
  tableNode.remove()
  tableNode.markDirty()
  $focusNode($getRoot())
}

const $cellContainsEmptyParagraph = (cell: TableCellNode) => {
  if (cell.getChildrenSize() !== 1) {
    return false
  }
  const firstChild = cell.getFirstChildOrThrow()
  if (!$isParagraphNode(firstChild) || !firstChild.isEmpty()) {
    return false
  }
  return true
}

/**
 * 判断是否可以合并单元格
 */
export const $canMergeTableCells = () => {
  const selection = $getSelection()
  if ($isTableSelection(selection)) {
    const { cols, rows } = computeTableSelectionCounts(selection)
    return cols > 1 || rows > 1
  }
  return false
}

/**
 * 判断是否可以拆分单元格
 */
export const $canUnmergeTableCells = () => {
  const selection = $getSelection()
  if (
    ($isRangeSelection(selection) && !selection.isCollapsed()) ||
    ($isTableSelection(selection) && !selection.anchor.is(selection.focus)) ||
    (!$isRangeSelection(selection) && !$isTableSelection(selection))
  ) {
    return false
  }
  const cell = $findCellNode(selection.anchor.getNode())
  if (!cell) {
    return false
  }
  return cell.__colSpan > 1 || cell.__rowSpan > 1
}

/**
 * 合并选区中的单元格
 */
export const $mergeTableCellsAtSelection = () => {
  const selection = $getSelection()
  if ($isTableSelection(selection)) {
    const { cols, rows } = computeTableSelectionCounts(selection)
    const nodes = selection.getNodes()
    let firstCell: null | TableCellNode = null
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i]
      if ($isTableCellNode(node)) {
        if (firstCell === null) {
          node.setColSpan(cols).setRowSpan(rows)
          firstCell = node
          const isEmpty = $cellContainsEmptyParagraph(node)
          let firstChild
          if (
            isEmpty &&
            $isParagraphNode((firstChild = node.getFirstChild()))
          ) {
            firstChild.remove()
          }
        } else if ($isTableCellNode(firstCell)) {
          const isEmpty = $cellContainsEmptyParagraph(node)
          if (!isEmpty) {
            firstCell.append(...node.getChildren())
          }
          node.remove()
        }
      }
    }
    if (firstCell !== null) {
      if (firstCell.getChildrenSize() === 0) {
        firstCell.append($createParagraphNode())
      }
      $selectLastDescendant(firstCell)
    }
  }
}

/**
 * 拆分选区中的单元格
 */
export const $unmergeTableCellsAtSelection = () => {
  const selection = $getSelection()
  if (!($isRangeSelection(selection) || $isTableSelection(selection))) return
  const anchor = selection.anchor.getNode()
  const [cell, row, table] = $getNodeTriplet(anchor)
  const colSpan = cell.__colSpan
  const rowSpan = cell.__rowSpan
  if (colSpan === 1 && rowSpan === 1) {
    return
  }
  const [map, cellMap] = $computeTableMap(table, cell, cell)
  const { startColumn, startRow } = cellMap
  // Create a heuristic for what the style of the unmerged cells should be
  // based on whether every row or column already had that state before the
  // unmerge.
  const baseColStyle = cell.__headerState & TableCellHeaderStates.COLUMN
  const colStyles = Array.from({ length: colSpan }, (_v, i) => {
    let colStyle = baseColStyle
    for (let rowIdx = 0; colStyle !== 0 && rowIdx < map.length; rowIdx++) {
      colStyle &= map[rowIdx][i + startColumn].cell.__headerState
    }
    return colStyle
  })
  const baseRowStyle = cell.__headerState & TableCellHeaderStates.ROW
  const rowStyles = Array.from({ length: rowSpan }, (_v, i) => {
    let rowStyle = baseRowStyle
    for (let colIdx = 0; rowStyle !== 0 && colIdx < map[0].length; colIdx++) {
      rowStyle &= map[i + startRow][colIdx].cell.__headerState
    }
    return rowStyle
  })

  if (colSpan > 1) {
    for (let i = 1; i < colSpan; i++) {
      cell.insertAfter(
        $createTableCellNode(colStyles[i] | rowStyles[0]).append(
          $createParagraphNode()
        )
      )
    }
    cell.setColSpan(1)
  }

  if (rowSpan > 1) {
    let currentRowNode
    for (let i = 1; i < rowSpan; i++) {
      const currentRow = startRow + i
      const currentRowMap = map[currentRow]
      currentRowNode = (currentRowNode || row).getNextSibling()
      if (!$isTableRowNode(currentRowNode)) {
        continue
      }
      let insertAfterCell: null | TableCellNode = null
      for (let column = 0; column < startColumn; column++) {
        const currentCellMap = currentRowMap[column]
        const currentCell = currentCellMap.cell
        if (currentCellMap.startRow === currentRow) {
          insertAfterCell = currentCell
        }
        if (currentCell.__colSpan > 1) {
          column += currentCell.__colSpan - 1
        }
      }
      if (insertAfterCell === null) {
        for (let j = colSpan - 1; j >= 0; j--) {
          $insertFirst(
            currentRowNode,
            $createTableCellNode(colStyles[j] | rowStyles[i]).append(
              $createParagraphNode()
            )
          )
        }
      } else {
        for (let j = colSpan - 1; j >= 0; j--) {
          insertAfterCell.insertAfter(
            $createTableCellNode(colStyles[j] | rowStyles[i]).append(
              $createParagraphNode()
            )
          )
        }
      }
    }
    cell.setRowSpan(1)
  }
}

/**
 * 高亮 node 所在的表格行
 */
export const $makeTableRowHighlight = (
  editor: LexicalEditor,
  tableNode: TableNode,
  rowIndex: number
) => {
  const tableRowNode = tableNode.getChildAtIndex(rowIndex)
  if (!$isTableRowNode(tableRowNode)) {
    return
  }
  const tableCellNodes = tableRowNode.getChildren().filter($isTableCellNode)
  const selection = $createTableSelectionFrom(
    tableNode,
    tableCellNodes[0],
    tableCellNodes[tableCellNodes.length - 1]
  )
  $updateTableHighlight(editor, tableNode, selection)
}

/**
 * 高亮 node 所在的表格列
 */
export const $makeTableColHighlight = (
  editor: LexicalEditor,
  tableNode: TableNode,
  colIndex: number
) => {
  const tableFirstRowNode = tableNode.getFirstChildOrThrow()
  const tableLastRowNode = tableNode.getLastChildOrThrow()
  if (
    !($isTableRowNode(tableFirstRowNode) && $isTableRowNode(tableLastRowNode))
  ) {
    return
  }
  const calcRealCellNode = (cellNodes: TableCellNode[]) => {
    // 根据 colSpan 计算实际的列索引
    let realColIndex = -1
    for (const cell of cellNodes) {
      realColIndex += cell.getColSpan()
      if (realColIndex >= colIndex) {
        return cell
      }
    }
    throw new Error('colIndex out of range')
  }
  const tableFirstRowCellNodes = tableFirstRowNode
    .getChildren()
    .filter($isTableCellNode)
  const tableFirstCellNode = calcRealCellNode(tableFirstRowCellNodes)
  const tableLastCellNodes = tableLastRowNode
    .getChildren()
    .filter($isTableCellNode)
  const tableLastCellNode = calcRealCellNode(tableLastCellNodes)
  const selection = $createTableSelectionFrom(
    tableNode,
    tableFirstCellNode,
    tableLastCellNode
  )
  $updateTableHighlight(editor, tableNode, selection)
}

/**
 * 高亮 node 所在的表格
 */
export const $makeTableHighlight = (
  editor: LexicalEditor,
  tableNode: TableNode
) => {
  const tableFirstRowNode = tableNode.getFirstChildOrThrow()
  const tableLastRowNode = tableNode.getLastChildOrThrow()
  if (
    !($isTableRowNode(tableFirstRowNode) && $isTableRowNode(tableLastRowNode))
  ) {
    return
  }
  const tableFirstCellNode =
    tableFirstRowNode.getFirstChildOrThrow() as TableCellNode
  const tableLastCellNode =
    tableLastRowNode.getLastChildOrThrow() as TableCellNode
  const selection = $createTableSelectionFrom(
    tableNode,
    tableFirstCellNode,
    tableLastCellNode
  )
  $updateTableHighlight(editor, tableNode, selection)
}

/**
 * 创建表格选区
 */
export function $createTableSelectionFrom(
  tableNode: TableNode,
  anchorCell: TableCellNode,
  focusCell: TableCellNode
): TableSelection {
  const prevSelection = $getSelection()
  const nextSelection = $isTableSelection(prevSelection)
    ? prevSelection.clone()
    : $createTableSelection()
  nextSelection.set(tableNode.getKey(), anchorCell.getKey(), focusCell.getKey())
  return nextSelection
}

/**
 * 更新表格高亮
 */
export const $updateTableHighlight = (
  editor,
  tableNode: TableNode,
  selection: TableSelection
) => {
  const tableObserver = getTableObserverFromOrThrow(editor, tableNode)
  tableObserver.$updateTableTableSelection(selection)
  $setSelection(selection)
}

export const $clearTableHighlight = (
  editor: LexicalEditor,
  tableNode: TableNode
) => {
  const tableObserver = getTableObserverFromOrThrow(editor, tableNode)
  tableObserver.$clearHighlight()
}

/**
 * 更新选区中的表格行的标记状态
 */
export const $updateTableRowMarkedAtSelection = (
  editor: LexicalEditor,
  marked: boolean
) => {
  const selection = $getSelection()
  if (!($isRangeSelection(selection) || $isTableSelection(selection))) return
  const tableNode = $getTableNodeAtSelection()
  if (!tableNode) return
  const allTableRowNodes = tableNode.getChildren().filter($isTableRowNode)
  const selectedTableRowNodes = $isRangeSelection(selection)
    ? [$findParentNodeOrThrow(selection.anchor.getNode(), $isTableRowNode)]
    : selection.getNodes().filter($isTableRowNode)
  const rowIndexes = selectedTableRowNodes.map(node =>
    allTableRowNodes.indexOf(node)
  )
  $updateTableRowMarked(editor, tableNode, rowIndexes, marked)
}

/**
 * 更新 table 中指定行的标记状态
 */
export const $updateTableRowMarked = (
  editor: LexicalEditor,
  tableNode: TableNode,
  rowIndexes: number[],
  marked: boolean
) => {
  const markedTableCellNodes: TableCellNode[] = []
  const tableRowNodes = tableNode.getChildren().filter($isTableRowNode)
  for (const [index, tableRowNode] of tableRowNodes.entries()) {
    if (rowIndexes.includes(index)) {
      markedTableCellNodes.push(
        ...tableRowNode.getChildren().filter($isTableCellNode)
      )
    }
  }
  editor.dispatchCommand(TABLE_MARK_UPDATE_COMMAND, {
    type: 'row',
    marked,
    cellNodes: markedTableCellNodes
  })
}

/**
 * 更新选区中的表格列的标记状态
 */
export const $updateTableColMarkedAtSelection = (
  editor: LexicalEditor,
  marked: boolean
) => {
  const tableNode = $getTableNodeAtSelection()
  if (!tableNode) return
  const colIndexSet = new Set<number>()
  const selection = $getSelection() as RangeSelection | TableSelection
  const selectedTableCellNodes = $getTableCellNodes(selection)
  const tableRowNodes = tableNode.getChildren().filter($isTableRowNode)
  for (const tableRowNode of tableRowNodes) {
    const rowTableCellNodes = tableRowNode
      .getChildren()
      .filter($isTableCellNode)
    for (const [index, tableCellNode] of rowTableCellNodes.entries()) {
      if (selectedTableCellNodes.includes(tableCellNode)) {
        colIndexSet.add(index)
      }
    }
  }
  $updateTableColMarked(editor, tableNode, Array.from(colIndexSet), marked)
}

/**
 * 更新 table 中指定列的标记状态
 */
export const $updateTableColMarked = (
  editor: LexicalEditor,
  tableNode: TableNode,
  colIndexes: number[],
  marked: boolean
) => {
  const markedTableCellNodes: TableCellNode[] = []
  const tableRowNodes = tableNode.getChildren().filter($isTableRowNode)
  for (const tableRowNode of tableRowNodes) {
    const rowTableCellNodes = tableRowNode
      .getChildren()
      .filter($isTableCellNode)
    for (const [index, tableCellNode] of rowTableCellNodes.entries()) {
      if (colIndexes.includes(index)) {
        markedTableCellNodes.push(tableCellNode)
      }
    }
  }
  editor.dispatchCommand(TABLE_MARK_UPDATE_COMMAND, {
    type: 'col',
    marked,
    cellNodes: markedTableCellNodes
  })
}

export const $updateTableMarkedAtSelection = (
  editor: LexicalEditor,
  marked: boolean
) => {
  const tableNode = $getTableNodeAtSelection()
  if (!tableNode) return
  $updateTableMarked(editor, tableNode, marked)
}

export const $updateTableMarked = (
  editor: LexicalEditor,
  tableNode: TableNode,
  marked: boolean
) => {
  const tableCellNodes = tableNode
    .getChildren()
    .filter($isTableRowNode)
    .flatMap(rowNode => rowNode.getChildren().filter($isTableCellNode))
  editor.dispatchCommand(TABLE_MARK_UPDATE_COMMAND, {
    type: 'selection',
    marked,
    cellNodes: tableCellNodes
  })
}

/**
 * 如果触发事件的 target 不是选区内的元素，则重置选区
 */
export const $resetTableSelectionOutofEventTarget = (
  editor: LexicalEditor,
  target: HTMLElement
) => {
  const targetLexicalNode = $getNearestNodeFromDOMNode(target)
  if (!targetLexicalNode) return
  const targetTableCellNode = $findCellNode(targetLexicalNode)
  if (!$isTableCellNode(targetTableCellNode)) return
  const tableNode = $findTableNode(targetTableCellNode)
  if (!tableNode) return
  const selection = $getSelection()
  const selectionTableCellNodes = $getTableCellNodes(selection)
  if (selectionTableCellNodes.includes(targetTableCellNode)) return
  const tableObserver = getTableObserverFromOrThrow(editor, tableNode)
  tableObserver.$clearHighlight()
  $focusNode(targetTableCellNode)
}

/**
 * 清空表格选区中的内容
 */
export const $clearTableContentAtSelection = () => {
  const selection = $getSelection()
  if ($isTableSelection(selection)) {
    const nodes = selection.getNodes()
    for (const node of nodes) {
      if ($isTableCellNode(node)) {
        for (const child of node.getChildren()) {
          child.remove()
        }
      }
    }
    return
  }
  if ($isRangeSelection(selection)) {
    const tableCellNode = $findParentNode(
      selection.anchor.getNode(),
      $isTableCellNode
    )
    if (tableCellNode) {
      for (const child of tableCellNode.getChildren()) {
        child.remove()
      }
    }
  }
}

/**
 * 插入表格
 */
export const $insertTable = (
  editor: LexicalEditor,
  payload: InsertTableCommandPayload
) => {
  // 禁止在表格中插入表格
  if ($isTableNodeFocusedAtSelection()) {
    return
  }
  editor.dispatchCommand(INSERT_TABLE_COMMAND, payload)
}
